import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { OnePieceCard } from '../../types/onepiece';
import { findOnePieceCard } from '../../utils/onepieceData';
import {
  useOnePieceCardOwnership,
  useUpdateOnePieceCardOwnership,
  useToggleOnePieceCardOwnership,
} from '../../hooks/useOnePieceCardOwnership';
import {
  useToggleWishlist,
  useToggleFavorite,
  useUserIntentsMap,
} from '../../hooks/useUserIntents';
import { useOnePieceSetCards } from '../../hooks/useMasterSets';
import CardDetailView from '../../components/CardDetailView/CardDetailView';
import type { CardOwnershipData } from '../../components/CardOwnershipForm/CardOwnershipForm';

const OnePieceCardDetails: React.FC = () => {
  const { setId, cardId } = useParams<{ setId: string; cardId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromSearch = location.state?.fromSearch;
  const searchUrl = location.state?.searchUrl;

  // Load cards for this set using TanStack Query
  const { data: cardsArray = [], isLoading: loading } = useOnePieceSetCards(setId);

  // Find the specific card
  const card = useMemo(() => {
    if (!cardsArray.length || !cardId) return null;
    return findOnePieceCard(cardsArray, cardId);
  }, [cardsArray, cardId]);

  const [showSnackbar, setShowSnackbar] = useState(false);

  // Use card ownership hooks
  const { data: ownership } = useOnePieceCardOwnership(cardId || '');
  const upsertOwnership = useUpdateOnePieceCardOwnership();
  const toggleOwnershipMutation = useToggleOnePieceCardOwnership();
  const toggleWishlistMutation = useToggleWishlist();
  const toggleFavoriteMutation = useToggleFavorite();
  const { intentsMap } = useUserIntentsMap(setId || '');

  const handleSave = async (data: CardOwnershipData) => {
    if (!cardId || !setId) return;

    try {
      await upsertOwnership.mutateAsync({
        cardId,
        data: {
          setId,
          ...data,
        },
      });
      setShowSnackbar(true);
      setTimeout(() => {
        navigate(`/onepiece/sets/${setId}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to save card ownership:', error);
    }
  };

  const handleToggle = () => {
    if (cardId && setId && ownership?.owned) {
      toggleOwnershipMutation.mutate({ cardId, setId });
    }
  };

  const breadcrumbs = fromSearch ? [
    { label: 'Search Results', onClick: () => navigate(searchUrl || '/search') },
    { label: card?.card_name || cardId || '', onClick: () => {} },
  ] : [
    { label: 'One Piece', onClick: () => navigate('/onepiece/sets') },
    { label: setId || '', onClick: () => navigate(`/onepiece/sets/${setId}`) },
  ];

  return (
    <CardDetailView
      card={card}
      loading={loading}
      gameType="onepiece"
      setId={setId || ''}
      cardId={cardId || ''}
      onBack={() => {
        if (fromSearch && searchUrl) {
          navigate(searchUrl);
        } else {
          navigate(`/onepiece/sets/${setId}`);
        }
      }}
      breadcrumbs={breadcrumbs}
      ownership={ownership}
      onToggleOwnership={handleToggle}
      onToggleWishlist={() => cardId && card && setId && toggleWishlistMutation.mutate({
        game: 'onepiece',
        setId,
        cardId: card.baseCardId || cardId,
        isWishlist: !intentsMap[card.baseCardId || cardId]?.isWishlist,
        cardName: card.justtcgName || card.name,
        imageUrl: card.images.small || card.images.large,
        rarity: card.rarity,
        color: card.color,
        cardNumber: card.number,
        baseCardId: card.baseCardId,
        imageId: cardId
      })}
      onToggleFavorite={() => cardId && card && setId && toggleFavoriteMutation.mutate({
        game: 'onepiece',
        setId,
        cardId: card.baseCardId || cardId,
        isFavorite: !intentsMap[card.baseCardId || cardId]?.isFavorite,
        cardName: card.justtcgName || card.name,
        imageUrl: card.images.small || card.images.large,
        rarity: card.rarity,
        color: card.color,
        cardNumber: card.number,
        baseCardId: card.baseCardId,
        imageId: cardId
      })}
      onSaveOwnership={handleSave}
      isSaving={upsertOwnership.isLoading}
      isToggling={toggleWishlistMutation.isPending || toggleFavoriteMutation.isPending}
      showSnackbar={showSnackbar}
      onCloseSnackbar={() => setShowSnackbar(false)}
    />
  );
};

export default OnePieceCardDetails;
